import {
  type ConditionalRoutingRule,
  type DraftTargetLink,
  type IdentifierRecord,
  type SectorId,
} from '@myplayer/schema';
import { useEffect, useState, type FormEvent } from 'react';
import { type AdminUserSession } from './auth-context.js';
import type {
  CancelScheduledChangeCallablePayload,
  CancelScheduledChangeCallableResult,
  SetScheduledChangeCallablePayload,
  SetScheduledChangeCallableResult,
} from './firebase.js';
import {
  cancelProductScheduledChangeAction,
  saveProductScheduledChangeAction,
} from './product-schedule-section.js';
import {
  buildUpdatedLinksList,
  resolvePendingFiredScheduledChange,
  resolveScheduledChange,
} from './products-helpers.js';

export function useProductEditState(params: {
  readonly session: AdminUserSession | null;
  readonly initialEditMode?: boolean | undefined;
  readonly selectedRecord: IdentifierRecord | null;
  readonly workingLinks: readonly DraftTargetLink[];
  readonly setSelectedKey: (key: string | null) => void;
  readonly setLinkFormError: (err: string | null) => void;
  readonly setLinkFormSuccess: (msg: string | null) => void;
  readonly setSavingDraft: (v: boolean) => void;
  readonly onSetScheduledChange?:
    | ((payload: SetScheduledChangeCallablePayload) => Promise<SetScheduledChangeCallableResult>)
    | undefined;
  readonly onCancelScheduledChange?:
    | ((
        payload: CancelScheduledChangeCallablePayload,
      ) => Promise<CancelScheduledChangeCallableResult>)
    | undefined;
  readonly onScheduledRulesUpdated: (
    identifierKey: string,
    nextRules: readonly ConditionalRoutingRule[],
    version: number,
  ) => void;
  readonly applyDraftLinksUpdate: (
    nextLinks: readonly DraftTargetLink[],
    nextRules?: IdentifierRecord['rules'],
    nextSector?: SectorId,
  ) => Promise<boolean>;
}) {
  const [isEditing, setIsEditing] = useState<boolean>(params.initialEditMode ?? false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState<boolean>(false);
  const [isExtraLinkModalOpen, setIsExtraLinkModalOpen] = useState<boolean>(false);
  const [editPrimaryUrl, setEditPrimaryUrl] = useState('');
  const [editScheduledEnabled, setEditScheduledEnabled] = useState(false);
  const [editScheduledDate, setEditScheduledDate] = useState('');
  const [editScheduledUrl, setEditScheduledUrl] = useState('');
  const [editSector, setEditSector] = useState<SectorId>('RETAIL_CPG');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [linkType, setLinkType] = useState<string>('gs1:pip');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkLanguage, setLinkLanguage] = useState('en');
  const [linkRegion, setLinkRegion] = useState('GLOBAL');
  const [linkIsDefault, setLinkIsDefault] = useState(false);

  const resetLinkForm = (): void => {
    setEditingIndex(null);
    setIsExtraLinkModalOpen(false);
    setLinkUrl('');
    setLinkTitle('');
    setLinkLanguage('en');
    setLinkRegion('GLOBAL');
    setLinkIsDefault(false);
    params.setLinkFormError(null);
  };

  const handleOpenAddLinkModal = (): void => {
    setEditingIndex(null);
    setLinkType('gs1:pip');
    setLinkUrl('');
    setLinkTitle('');
    setLinkLanguage('en');
    setLinkRegion('GLOBAL');
    setLinkIsDefault(false);
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    setIsExtraLinkModalOpen(true);
  };

  const handleSaveLinkForm = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    const updatedResult = buildUpdatedLinksList({
      workingLinks: params.workingLinks,
      editingIndex,
      linkType,
      linkUrl,
      linkTitle,
      linkLanguage,
      linkRegion,
      linkIsDefault,
    });
    if (!updatedResult.ok) {
      params.setLinkFormError(updatedResult.error);
      return;
    }
    if (await params.applyDraftLinksUpdate(updatedResult.links)) {
      resetLinkForm();
    }
  };

  const handleStartEditLink = (index: number): void => {
    const target = params.workingLinks[index];
    if (!target) return;
    const mainIdx = params.workingLinks.findIndex((l) => l.removed !== true && l.defaultLink);
    const firstActiveIdx = params.workingLinks.findIndex((l) => l.removed !== true);
    const effectiveMainIdx = mainIdx >= 0 ? mainIdx : firstActiveIdx;
    const isMainLink =
      target.removed !== true && (target.defaultLink || index === effectiveMainIdx);
    const pendingFired =
      isMainLink && params.selectedRecord
        ? resolvePendingFiredScheduledChange(params.selectedRecord)
        : null;
    setEditingIndex(index);
    setLinkType(target.linkType);
    setLinkUrl(pendingFired?.targetHref ?? target.href);
    setLinkTitle(target.title);
    setLinkLanguage(target.hreflang.join(', '));
    setLinkRegion(target.context.join(', '));
    setLinkIsDefault(target.defaultLink);
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    setIsExtraLinkModalOpen(true);
  };

  const syncEditFormFromRecord = (rec: IdentifierRecord): void => {
    const recLinks = rec.draft?.links ?? rec.links ?? [];
    const pendingFired = resolvePendingFiredScheduledChange(rec);
    const defaultLink =
      recLinks.find((l) => l.removed !== true && l.defaultLink) ??
      recLinks.find((l) => l.removed !== true);
    const primaryHref =
      pendingFired?.targetHref ??
      defaultLink?.href ??
      rec.draft?.defaultUrl ??
      rec.defaultUrl ??
      '';
    setEditPrimaryUrl(primaryHref);
    const scheduled = resolveScheduledChange(rec);
    if (scheduled) {
      setEditScheduledEnabled(true);
      setEditScheduledDate(scheduled.validFrom.slice(0, 16));
      setEditScheduledUrl(scheduled.targetHref);
    } else {
      setEditScheduledEnabled(false);
      setEditScheduledDate('');
      setEditScheduledUrl('');
    }
    setEditSector(rec.sector ?? 'RETAIL_CPG');
  };

  useEffect(() => {
    if (!params.selectedRecord) return;
    syncEditFormFromRecord(params.selectedRecord);
  }, [params.selectedRecord?.identifierKey]);

  useEffect(() => {
    if (!params.selectedRecord || !isEditing) return;
    syncEditFormFromRecord(params.selectedRecord);
  }, [isEditing, params.selectedRecord?.identifierKey]);

  useEffect(() => {
    if (!params.selectedRecord || isEditing || isScheduleModalOpen || isExtraLinkModalOpen) return;
    syncEditFormFromRecord(params.selectedRecord);
  }, [params.selectedRecord, isEditing, isScheduleModalOpen, isExtraLinkModalOpen]);

  const handleOpenProduct = (identifierKey: string): void => {
    params.setSelectedKey(identifierKey);
    setIsEditing(false);
    setIsScheduleModalOpen(false);
    setIsExtraLinkModalOpen(false);
    setShowAdvanced(false);
    setEditingIndex(null);
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
  };

  const handleStartEditingProduct = (): void => {
    if (!params.selectedRecord) return;
    syncEditFormFromRecord(params.selectedRecord);
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    setShowAdvanced(false);
    setIsEditing(true);
  };

  const handleCancelEditingProduct = (): void => {
    setIsEditing(false);
    setShowAdvanced(false);
    setEditingIndex(null);
    params.setLinkFormError(null);
    if (params.selectedRecord) {
      syncEditFormFromRecord(params.selectedRecord);
    }
  };

  const handleOpenScheduleModal = (): void => {
    if (!params.selectedRecord) return;
    syncEditFormFromRecord(params.selectedRecord);
    setEditScheduledEnabled(true);
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    setIsScheduleModalOpen(true);
  };

  const handleOpenScheduleForUrl = (targetHref: string): void => {
    if (!params.selectedRecord) return;
    syncEditFormFromRecord(params.selectedRecord);
    setEditScheduledEnabled(true);
    setEditScheduledUrl(targetHref);
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    setIsScheduleModalOpen(true);
  };

  const handleSetMainLink = async (index: number): Promise<boolean> => {
    const target = params.workingLinks[index];
    if (!target || target.removed === true) return false;
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    const nextLinks = params.workingLinks.map((l, idx) => ({
      ...l,
      defaultLink: idx === index && l.removed !== true,
    }));
    return params.applyDraftLinksUpdate(nextLinks);
  };

  const handleCloseScheduleModal = (): void => {
    setIsScheduleModalOpen(false);
    params.setLinkFormError(null);
    if (params.selectedRecord) {
      syncEditFormFromRecord(params.selectedRecord);
    }
  };

  const handleSaveSchedule = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!params.selectedRecord) return;
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    const saved = await saveProductScheduledChangeAction({
      session: params.session,
      selectedRecord: params.selectedRecord,
      editScheduledDate,
      editScheduledUrl,
      onSetScheduledChange: params.onSetScheduledChange,
      setLinkFormError: params.setLinkFormError,
      setLinkFormSuccess: params.setLinkFormSuccess,
      setSavingDraft: params.setSavingDraft,
      onScheduledRulesUpdated: params.onScheduledRulesUpdated,
    });
    if (saved) {
      setIsScheduleModalOpen(false);
    }
  };

  const handleClearSchedule = async (): Promise<void> => {
    if (!params.selectedRecord) return;
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    const saved = await cancelProductScheduledChangeAction({
      session: params.session,
      selectedRecord: params.selectedRecord,
      onCancelScheduledChange: params.onCancelScheduledChange,
      setLinkFormError: params.setLinkFormError,
      setLinkFormSuccess: params.setLinkFormSuccess,
      setSavingDraft: params.setSavingDraft,
      onScheduledRulesUpdated: params.onScheduledRulesUpdated,
    });
    if (saved) {
      setIsScheduleModalOpen(false);
      setEditScheduledEnabled(false);
      setEditScheduledDate('');
      setEditScheduledUrl('');
    }
  };

  const handleSaveProductEdit = async (e: FormEvent): Promise<boolean> => {
    e.preventDefault();
    if (!params.selectedRecord) return false;
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);

    const trimmedPrimary = editPrimaryUrl.trim();
    if (trimmedPrimary.length === 0 || !trimmedPrimary.startsWith('https://')) {
      params.setLinkFormError(
        'Please enter a valid web address starting with https:// (for example, https://example.com/product).',
      );
      return false;
    }

    const displayTitle =
      params.selectedRecord.draft?.title ??
      params.selectedRecord.title ??
      params.selectedRecord.primaryKey;
    const matchedIdx = params.workingLinks.findIndex(
      (l) => l.removed !== true && l.href === trimmedPrimary,
    );
    const defaultIdx = params.workingLinks.findIndex((l) => l.removed !== true && l.defaultLink);
    const nextLinks: DraftTargetLink[] =
      matchedIdx >= 0
        ? params.workingLinks.map((l, idx) => ({
            ...l,
            defaultLink: idx === matchedIdx && l.removed !== true,
          }))
        : defaultIdx >= 0
          ? params.workingLinks.map((l, idx) =>
              idx === defaultIdx ? { ...l, href: trimmedPrimary } : l,
            )
          : [
              {
                linkType: 'gs1:pip',
                href: trimmedPrimary,
                title: displayTitle,
                type: 'text/html',
                hreflang: ['en'],
                context: ['GLOBAL'],
                defaultLink: true,
              },
              ...params.workingLinks.map((l) => ({ ...l, defaultLink: false })),
            ];

    const draftRules = params.selectedRecord.draft?.rules;
    const existingRules =
      draftRules && draftRules.length > 0 ? draftRules : (params.selectedRecord.rules ?? []);
    const saved = await params.applyDraftLinksUpdate(nextLinks, existingRules, editSector);
    if (saved) {
      setIsEditing(false);
      setShowAdvanced(false);
      setEditingIndex(null);
    }
    return saved;
  };

  return {
    isEditing,
    setIsEditing,
    isScheduleModalOpen,
    setIsScheduleModalOpen,
    isExtraLinkModalOpen,
    setIsExtraLinkModalOpen,
    editPrimaryUrl,
    setEditPrimaryUrl,
    editScheduledEnabled,
    setEditScheduledEnabled,
    editScheduledDate,
    setEditScheduledDate,
    editScheduledUrl,
    setEditScheduledUrl,
    editSector,
    setEditSector,
    showAdvanced,
    setShowAdvanced,
    editingIndex,
    setEditingIndex,
    linkType,
    setLinkType,
    linkUrl,
    setLinkUrl,
    linkTitle,
    setLinkTitle,
    linkLanguage,
    setLinkLanguage,
    linkRegion,
    setLinkRegion,
    linkIsDefault,
    setLinkIsDefault,
    resetLinkForm,
    handleOpenAddLinkModal,
    handleSaveLinkForm,
    handleStartEditLink,
    handleOpenProduct,
    handleStartEditingProduct,
    handleCancelEditingProduct,
    handleOpenScheduleModal,
    handleOpenScheduleForUrl,
    handleSetMainLink,
    handleCloseScheduleModal,
    handleSaveSchedule,
    handleClearSchedule,
    handleSaveProductEdit,
  };
}
